import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { listMyEvents, type OrganizerEvent } from '../../lib/organizer';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort, formatMoney, formatTime } from '../../lib/format';
import { useEventPaths } from '../../admin/routeBase';
import './OrganizerPages.css';

const STATUS_COPY: Record<string, string> = {
  draft: 'Draft',
  published: 'On sale',
  live: 'Live',
  closed: 'Closed',
  cancelled: 'Cancelled',
  postponed: 'Postponed',
};

function EventRow({ event }: { event: OrganizerEvent }) {
  const paths = useEventPaths();
  const time = formatTime(event.time);
  const sellThrough = event.capacity > 0
    ? Math.round((event.ticketsSold / event.capacity) * 100)
    : 0;

  return (
    <article className="org-event">
      <div className="org-event__main">
        <div className="org-event__head">
          <Link to={paths.manage(event.id)} className="org-event__title">
            {event.title}
          </Link>
          <span className={`org-status org-status--${event.status}`}>
            {STATUS_COPY[event.status] ?? event.status}
          </span>
        </div>

        <p className="org-event__meta">
          {formatDateShort(event.date)}
          {time ? ` · ${time}` : ''}
          {event.venueName ? ` · ${event.venueName}` : ''}
          {event.city ? `, ${event.city}` : ''}
        </p>

        {/* The compliance block is the single most common reason a draft is stuck,
            so it is stated on the card rather than discovered on a failed publish. */}
        {event.blockedReason === 'compliance_incomplete' && (
          <p className="org-blocked">
            <span aria-hidden="true">⚠</span> Compliance incomplete, cannot publish yet
          </p>
        )}
        {event.status === 'draft' && event.canPublish && (
          <p className="org-ready">✓ Compliance cleared, ready to publish</p>
        )}
      </div>

      <dl className="org-event__stats">
        <div>
          <dt>Sold</dt>
          <dd className="mono">
            {event.ticketsSold}
            {event.capacity > 0 && (
              <span className="org-event__of"> / {event.capacity}</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Sell-through</dt>
          <dd className="mono">{event.capacity > 0 ? `${sellThrough}%` : ' | '}</dd>
        </div>
        <div>
          <dt>Revenue</dt>
          <dd className="mono">{formatMoney(event.revenueCents)}</dd>
        </div>
        <div>
          <dt>Tiers</dt>
          <dd className="mono">{event.tierCount}</dd>
        </div>
      </dl>

      <div className="org-event__actions">
        <Link to={paths.manage(event.id)} className="button button--secondary">
          Manage
        </Link>
      </div>
    </article>
  );
}

export function OrganizerDashboard() {
  const paths = useEventPaths();
  const [events, setEvents] = useState<OrganizerEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listMyEvents()
      .then((r) => { if (!cancelled) setEvents(r); })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load your events.');
        setEvents([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const drafts = events?.filter((e) => e.status === 'draft') ?? [];
  const blocked = drafts.filter((e) => e.blockedReason).length;
  const onSale = events?.filter((e) => e.status === 'published' || e.status === 'live') ?? [];
  const totalRevenue = events?.reduce((sum, e) => sum + e.revenueCents, 0) ?? 0;
  const totalSold = events?.reduce((sum, e) => sum + e.ticketsSold, 0) ?? 0;

  return (
    <div className="org-page">
      <header className="org-header">
        <div>
          <h1 className="org-title display">Your events</h1>
          <p className="org-lede">Create, manage, and take a show on sale.</p>
        </div>
        <Link to={paths.create} className="button button--primary org-cta">
          + Create event
        </Link>
      </header>

      {!loading && events && events.length > 0 && (
        <div className="org-summary">
          <div className="org-summary__stat">
            <span className="org-summary__value mono">{onSale.length}</span>
            <span className="org-summary__label">on sale</span>
          </div>
          <div className="org-summary__stat">
            <span className="org-summary__value mono">{drafts.length}</span>
            <span className="org-summary__label">drafts</span>
            {blocked > 0 && (
              <span className="org-summary__sub">{blocked} blocked on compliance</span>
            )}
          </div>
          <div className="org-summary__stat">
            <span className="org-summary__value mono">{totalSold}</span>
            <span className="org-summary__label">tickets sold</span>
          </div>
          <div className="org-summary__stat">
            <span className="org-summary__value mono org-summary__value--gold">
              {formatMoney(totalRevenue)}
            </span>
            <span className="org-summary__label">gross revenue</span>
          </div>
        </div>
      )}

      {loading && <p className="org-status">Loading your events…</p>}
      {error && <p className="org-status org-error" role="alert">{error}</p>}

      {!loading && events && events.length === 0 && (
        <div className="org-empty">
          <h2>No events yet</h2>
          <p>
            Create your first event. It starts as a draft, nothing goes on sale until you
            publish it, and publishing needs compliance cleared first.
          </p>
          <Link to={paths.create} className="button button--primary">
            Create your first event
          </Link>
        </div>
      )}

      <div className="org-list">
        {events?.map((event) => <EventRow key={event.id} event={event} />)}
      </div>
    </div>
  );
}
